import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  type OnDestroy,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { QuizQuestionResponse } from '../../../models/quiz';
import { MetadataService } from '../../../services/metadata/metadata';
import { QuizService } from '../../../services/quiz/quiz';

/** A choice as shown to the student, with the original index the backend grades against. */
interface ShownChoice {
  text: string;
  value: number;
}

type QuizTakingPhase = 'confirm' | 'question' | 'time-expired' | 'result' | 'review';

const SECONDS_PER_MINUTE = 60;
const TICK_MS = 1000;
const MIN_GRADE = 0;
const MAX_GRADE = 10;
const DECIMAL_BASE = 10;
const GRADE_DECIMAL_PLACES = 3;
const GRADE_ROUNDING_FACTOR = DECIMAL_BASE ** GRADE_DECIMAL_PLACES;
const TIME_WARNING_RATIO = 0.25;
const SECONDS_STRING_LENGTH = 2;

@Component({
  selector: 'nr-quiz-taking-dialog',
  imports: [TranslatePipe],
  templateUrl: './quiz-taking-dialog.html',
  styleUrl: './quiz-taking-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class QuizTakingDialogComponent implements OnDestroy {
  private readonly quizService = inject(QuizService);

  private readonly metadataService = inject(MetadataService);

  private readonly maxAttempts = computed(() =>
    this.metadataService.canAddAttempts() ? this.quiz()?.maxAttempts : undefined,
  );
  private readonly timerHandle: ReturnType<typeof setInterval>;

  private readonly reviewAnswerValue = computed(() => {
    const questionId = this.reviewQuestion()?.id;
    if (!questionId) return undefined;
    return this.myAnswer()?.values.find((value) => value.questionId === questionId);
  });

  readonly quiz = this.quizService.quizToAnswer;

  readonly myAnswer = computed(() => {
    const quizId = this.quiz()?.id;
    if (!quizId) return undefined;
    return this.quizService.quizzes().find((item) => item.quiz.id === quizId)?.myAnswer;
  });

  readonly phase = signal<QuizTakingPhase>(this.initialPhase());

  readonly isInformationTitle = computed(
    () => this.phase() === 'confirm' || this.phase() === 'time-expired',
  );

  readonly isStandaloneQuestion = computed(() => {
    const kind = this.quiz()?.materialKind;
    return kind === 'multi' || kind === 'simple';
  });

  readonly dialogSize = computed(() => (this.isInformationTitle() ? 'medium' : 'large'));

  readonly attemptsRemaining = computed(() => {
    const max = this.maxAttempts();
    if (!max) return undefined;
    return Math.max(0, max - (this.myAnswer()?.attempt ?? 0));
  });

  readonly totalSeconds = computed(() => (this.quiz()?.time ?? 0) * SECONDS_PER_MINUTE);

  readonly elapsedSeconds = computed(() => {
    const remaining = this.remainingSeconds();
    if (remaining === undefined) return undefined;
    return this.totalSeconds() - remaining;
  });

  readonly elapsedTimeDisplay = computed(() => {
    const elapsed = this.elapsedSeconds();
    return elapsed === undefined
      ? undefined
      : QuizTakingDialogComponent.formatMinutesSeconds(elapsed);
  });

  readonly totalTimeDisplay = computed(() =>
    QuizTakingDialogComponent.formatMinutesSeconds(this.totalSeconds()),
  );

  readonly isTimeRunningLow = computed(() => {
    const remaining = this.remainingSeconds();
    const total = this.totalSeconds();
    if (remaining === undefined || !total) return false;
    return remaining <= total * TIME_WARNING_RATIO;
  });

  readonly gradeOutOfTen = computed(() => {
    const answer = this.myAnswer();
    const quiz = this.quiz();
    if (!answer || !quiz || quiz.questions.length === 0) return undefined;
    if (quiz.materialKind === 'simple') return undefined;
    if (answer.score != null) return QuizTakingDialogComponent.roundGrade(answer.score);
    if (this.isGradePending()) return undefined;
    const correctCount = answer.values.filter(
      (value) => value.value !== null && value.value === value.correctChoice,
    ).length;
    return QuizTakingDialogComponent.roundGrade((correctCount / quiz.questions.length) * MAX_GRADE);
  });

  readonly canRetry = computed(() => {
    const maxAttempts = this.maxAttempts();
    const answer = this.myAnswer();
    if (!maxAttempts || !answer) return false;
    return !answer.finished && maxAttempts > (answer.attempt ?? 1);
  });

  readonly currentQuestionIndex = signal(0);

  readonly reviewIndex = signal(0);

  readonly isSubmitting = signal(false);

  readonly values = signal<Record<string, number | null>>({});

  readonly remainingSeconds = signal<number | undefined>(undefined);

  readonly openEndedAnswerText = signal('');

  readonly currentQuestion = computed(() => this.quiz()?.questions[this.currentQuestionIndex()]);

  readonly currentChoices = computed(() =>
    QuizTakingDialogComponent.shownChoices(this.currentQuestion()),
  );

  readonly hasAnsweredCurrentQuestion = computed(() => {
    const question = this.currentQuestion();
    if (!question) return false;
    if (this.quiz()?.materialKind === 'simple') {
      return this.openEndedAnswerText().trim().length > 0;
    }
    return this.values()[question.id] != null;
  });

  readonly isGradePending = computed(() => {
    const answer = this.myAnswer();
    const kind = this.quiz()?.materialKind;
    if (!answer) return false;
    // Answered offline: the server grades it once synced.
    if (answer.pendingSync) return true;
    if (!kind) return false;
    if (kind === 'simple') return answer.score == null;
    // An attempt in progress comes back without correctChoice, but its score is already known.
    if (kind === 'multi') {
      return answer.score == null && answer.values[0]?.correctChoice === undefined;
    }
    return false;
  });

  readonly aiGrade = computed(() => {
    const answer = this.myAnswer();
    if (this.quiz()?.materialKind !== 'simple' || answer?.score == null) return undefined;
    const clampedScore = Math.min(MAX_GRADE, Math.max(MIN_GRADE, answer.score));
    return QuizTakingDialogComponent.roundGrade(clampedScore);
  });

  readonly reviewQuestion = computed(() => this.quiz()?.questions[this.reviewIndex()]);

  readonly reviewChoices = computed(() =>
    QuizTakingDialogComponent.shownChoices(this.reviewQuestion()),
  );

  readonly reviewCorrectChoice = computed(() => {
    const correctChoice = this.reviewAnswerValue()?.correctChoice;
    return correctChoice != null && correctChoice >= 0 ? correctChoice : undefined;
  });

  readonly reviewSelectedChoice = computed(() => this.reviewAnswerValue()?.value);

  readonly reviewIsSelectedCorrect = computed(() => {
    const value = this.reviewAnswerValue();
    if (!value) return undefined;
    if (value.score != null) return value.score > 0;
    const correctChoice = this.reviewCorrectChoice();
    return correctChoice == null ? undefined : value.value === correctChoice;
  });

  readonly reviewKnownCorrectChoice = computed(() => {
    const correctChoice = this.reviewCorrectChoice();
    if (correctChoice != null) return correctChoice;
    return this.reviewIsSelectedCorrect() === true ? this.reviewSelectedChoice() : undefined;
  });

  readonly isLastQuestion = computed(
    () => this.currentQuestionIndex() === (this.quiz()?.questions.length ?? 1) - 1,
  );

  readonly isLastReviewQuestion = computed(
    () => this.reviewIndex() === (this.quiz()?.questions.length ?? 1) - 1,
  );

  constructor() {
    this.timerHandle = setInterval(() => {
      this.tick();
    }, TICK_MS);
  }

  ngOnDestroy(): void {
    clearInterval(this.timerHandle);
  }

  startAttempt(): void {
    const quiz = this.quiz();
    if (!quiz) return;
    if (quiz.time) this.remainingSeconds.set(quiz.time * SECONDS_PER_MINUTE);
    this.phase.set('question');
  }

  selectAnswer(questionId: string, choiceIndex: number): void {
    this.values.update((current) => ({ ...current, [questionId]: choiceIndex }));
  }

  updateOpenEndedAnswerText(value: string): void {
    this.openEndedAnswerText.set(value);
  }

  goToPreviousQuestion(): void {
    this.currentQuestionIndex.update((index) => Math.max(0, index - 1));
  }

  goToNextQuestion(): void {
    if (!this.hasAnsweredCurrentQuestion()) return;
    this.currentQuestionIndex.update((index) => index + 1);
  }

  async saveAnswers(): Promise<void> {
    if (!this.hasAnsweredCurrentQuestion() || this.isSubmitting()) return;
    await this.submitAttempt();
  }

  async acknowledgeTimeExpired(): Promise<void> {
    if (this.isSubmitting()) return;
    await this.submitAttempt();
  }

  tryAgain(): void {
    this.currentQuestionIndex.set(0);
    this.values.set({});
    this.openEndedAnswerText.set('');
    this.remainingSeconds.set(undefined);
    const quiz = this.quiz();
    this.phase.set(quiz?.time ? 'confirm' : 'question');
  }

  viewAnswers(): void {
    this.reviewIndex.set(0);
    this.phase.set('review');
  }

  goToPreviousReviewQuestion(): void {
    this.reviewIndex.update((index) => Math.max(0, index - 1));
  }

  goToNextReviewQuestion(): void {
    this.reviewIndex.update((index) => index + 1);
  }

  close(): void {
    this.quizService.closeQuizAnswer();
  }

  async sendAssignment(): Promise<void> {
    const answerId = this.myAnswer()?.id;
    if (!answerId) return;
    await this.quizService.finishQuizAttempt(answerId);
  }

  private tick(): void {
    if (this.phase() !== 'question') return;
    const remaining = this.remainingSeconds();
    if (remaining === undefined) return;
    if (remaining <= 1) {
      this.remainingSeconds.set(0);
      this.phase.set('time-expired');
      return;
    }
    this.remainingSeconds.set(remaining - 1);
  }

  private async submitAttempt(): Promise<void> {
    const quiz = this.quiz();
    if (!quiz) return;
    this.isSubmitting.set(true);
    try {
      const answer =
        quiz.materialKind && quiz.materialKind !== 'quiz'
          ? await this.quizService.submitQuestionAnswer(quiz, {
              value:
                quiz.materialKind === 'simple'
                  ? this.openEndedAnswerText()
                  : String(this.values()[quiz.questions[0].id] ?? ''),
              existingAnswerId: this.myAnswer()?.id,
              attempt: this.nextAttempt(),
            })
          : await this.quizService.submitQuizAnswer(quiz, {
              values: quiz.questions.map((question) => ({
                questionId: question.id,
                value: this.values()[question.id] ?? null,
              })),
              existingAnswerId: this.myAnswer()?.id,
              attempt: this.nextAttempt(),
            });
      if (!answer) return;
      this.phase.set('result');
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private nextAttempt(): number | undefined {
    const maxAttempts = this.maxAttempts();
    if (!maxAttempts || maxAttempts <= 1) return undefined;
    return (this.myAnswer()?.attempt ?? 0) + 1;
  }

  private initialPhase(): QuizTakingPhase {
    if (this.myAnswer()) return 'result';
    return this.quiz()?.time ? 'confirm' : 'question';
  }

  private static shownChoices(question: QuizQuestionResponse | undefined): ShownChoice[] {
    if (!question) return [];
    const { choiceOrder } = question;
    return question.choices.map((choice, index) => ({
      text: choice.text,
      value: choiceOrder?.[index] ?? index,
    }));
  }

  private static roundGrade(value: number | undefined): number | undefined {
    if (value == null) return undefined;
    return Math.round(value * GRADE_ROUNDING_FACTOR) / GRADE_ROUNDING_FACTOR;
  }

  private static formatMinutesSeconds(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
    const seconds = totalSeconds % SECONDS_PER_MINUTE;
    return `${minutes}:${seconds.toString().padStart(SECONDS_STRING_LENGTH, '0')}`;
  }
}
